import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import {
  GET_CHANNEL_CONNECTIONS,
  GET_MY_CHANNEL,
  type ChannelConnectionItem,
  type ChannelConnectionType,
  type GetChannelConnectionsData,
  type GetChannelConnectionsVariables,
  type GetMyChannelData,
} from '@jwp/ott-common/src/graphql/query/channel.queries';
import {
  FOLLOW_CHANNEL,
  UNFOLLOW_CHANNEL,
  type FollowChannelData,
  type FollowChannelVariables,
  type UnfollowChannelData,
  type UnfollowChannelVariables,
} from '@jwp/ott-common/src/graphql/mutation/channel.mutations';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useFollowChannelStore } from '@jwp/ott-common/src/stores/FollowChannelStore';
import { showToast } from '@jwp/ott-common/src/stores/ToastStore';
import { PERMISSIONS, hasPermission } from '@jwp/ott-common/src/constants';
import { resolveMediaUrl } from '@jwp/ott-common/src/utils/mediaUrl';
import { channelURL } from '@jwp/ott-common/src/utils/urlFormatting';
import ArrowLeft from '@jwp/ott-theme/assets/icons/arrow_left.svg?react';
import MoreIcon from '@jwp/ott-theme/assets/icons/more.svg?react';
import ShareIcon from '@jwp/ott-theme/assets/icons/Share_icon.svg?react';
import FlagIcon from '@jwp/ott-theme/assets/icons/Flag.svg?react';
import FollowIcon from '@jwp/ott-theme/assets/icons/FollowIcon.svg?react';
import UnfollowIcon from '@jwp/ott-theme/assets/icons/UnfollowIcon.svg?react';
import IllustrationIcon from '@jwp/ott-theme/assets/icons/illustration.svg?react';
import useDebounce from '@jwp/ott-hooks-react/src/useDebounce';
import Icon from '../../components/Icon/Icon';
import IconButton from '../../components/IconButton/IconButton';
import Spinner from '../../components/Spinner/Spinner';
import ChannelSearchBar from '../../components/ChannelSearch/ChannelSearch';
import ReportChannelModal from '../../components/ReportsModal/ReportChannelModal';

import styles from './AllSubscribersPage.module.scss';

const PAGE_SIZE = 20;
const DEFAULT_AVATAR = '/static/default-avatar.png';

const getErrorMessage = (err: unknown, fallback: string) => {
  const graphqlError = err as { graphQLErrors?: Array<{ message?: string }>; message?: string };
  return graphqlError?.graphQLErrors?.[0]?.message || graphqlError?.message || fallback;
};

const hasOwnChannel = (item: ChannelConnectionItem) => Boolean(item.chId && (item.chHandle || item.chName));

const displayName = (item: ChannelConnectionItem) =>
  item.chName || item.userInfo?.uFullName || item.chHandle || '';

const displayHandle = (item: ChannelConnectionItem) => {
  if (item.chHandle) {
    return item.chHandle.startsWith('@') ? item.chHandle : `@${item.chHandle}`;
  }
  return '';
};

const avatarSrc = (item: ChannelConnectionItem) =>
  resolveMediaUrl(item.chAvatarUrl, '') || resolveMediaUrl(item.userInfo?.uAvatarUrl, DEFAULT_AVATAR) || DEFAULT_AVATAR;

const AllSubscribersPage = () => {
  const { t } = useTranslation(['user', 'common']);
  const navigate = useNavigate();
  const { chId = '' } = useParams<{ chId: string }>();
  const user = useAccountStore((state) => state.user);
  const userId = user?.id ?? null;
  const canReportChannel = hasPermission(user, PERMISSIONS.CREATE_CHANNEL_REPORT);
  const myChannelId = useAccountStore((state) => state.myChannelId);
  const setFollowStatus = useFollowChannelStore((state) => state.setStatus);
  const setFollowPending = useFollowChannelStore((state) => state.setPending);
  const hydrateFollow = useFollowChannelStore((state) => state.hydrate);

  const [connectionType, setConnectionType] = useState<ChannelConnectionType>('FOLLOWING');
  const [searchActive, setSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [menuItemId, setMenuItemId] = useState<string | null>(null);
  const [reportItem, setReportItem] = useState<ChannelConnectionItem | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [menuCoords, setMenuCoords] = useState<{ top: number; right: number } | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const debouncedSetSearch = useDebounce((query: string) => {
    setDebouncedSearch(query);
  }, 350);

  const { data: channelData } = useQuery<GetMyChannelData>(GET_MY_CHANNEL, {
    client: getAuthApolloClient(),
    variables: { chId },
    skip: !chId,
    fetchPolicy: 'cache-first',
  });
  const pageChannel = channelData?.getMyChannel?.channels?.[0];
  const isOwnChannel = Boolean(userId && pageChannel?.userInfo?.uId === userId) || myChannelId === chId;

  const queryVariables = useMemo<GetChannelConnectionsVariables>(
    () => ({
      input: {
        chId,
        limit: PAGE_SIZE,
        offset: 0,
        search: debouncedSearch.trim(),
        type: connectionType,
      },
    }),
    [chId, connectionType, debouncedSearch],
  );

  const { data, loading, fetchMore, refetch, networkStatus } = useQuery<GetChannelConnectionsData, GetChannelConnectionsVariables>(
    GET_CHANNEL_CONNECTIONS,
    {
      client: getAuthApolloClient(),
      variables: queryVariables,
      skip: !chId,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    },
  );

  const connections = data?.getChannelConnections;
  const items = connections?.items || [];
  const totalCount = connections?.totalCount ?? 0;
  const followingCount = connections?.followingCount ?? 0;
  const followersCount = connections?.followersCount ?? 0;
  const isInitialLoading = loading && !data;

  useEffect(() => {
    items.forEach((item) => {
      if (item.chId) {
        hydrateFollow(item.chId, item.isFollowedByMe);
      }
    });
  }, [hydrateFollow, items]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || dropdownRef.current?.contains(target)) {
        return;
      }
      setMenuItemId(null);
      setMenuCoords(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!menuItemId) {
      return;
    }
    const closeMenu = () => {
      setMenuItemId(null);
      setMenuCoords(null);
    };
    const timeoutId = window.setTimeout(() => {
      window.addEventListener('scroll', closeMenu, true);
      window.addEventListener('resize', closeMenu);
    }, 0);
    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [menuItemId]);

  const handleSearchQueryChange = useCallback(
    (query: string) => {
      setSearchQuery(query);
      debouncedSetSearch(query);
    },
    [debouncedSetSearch],
  );

  const handleLoadMore = useCallback(async () => {
    if (!chId || loadingMore || items.length >= totalCount) {
      return;
    }
    setLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          input: {
            chId,
            limit: PAGE_SIZE,
            offset: items.length,
            search: debouncedSearch.trim(),
            type: connectionType,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.getChannelConnections) {
            return prev;
          }
          const existingIds = new Set((prev.getChannelConnections?.items || []).map((item) => item.chId || item.userInfo?.uId));
          const nextItems = (fetchMoreResult.getChannelConnections.items || []).filter(
            (item) => !existingIds.has(item.chId || item.userInfo?.uId),
          );
          return {
            getChannelConnections: {
              ...fetchMoreResult.getChannelConnections,
              items: [...(prev.getChannelConnections?.items || []), ...nextItems],
            },
          };
        },
      });
    } finally {
      setLoadingMore(false);
    }
  }, [chId, connectionType, debouncedSearch, fetchMore, items.length, loadingMore, totalCount]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) {
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        void handleLoadMore();
      }
    }, { rootMargin: '160px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [handleLoadMore]);

  const [followChannel] = useMutation<FollowChannelData, FollowChannelVariables>(FOLLOW_CHANNEL, {
    client: getAuthApolloClient(),
  });
  const [unfollowChannel] = useMutation<UnfollowChannelData, UnfollowChannelVariables>(UNFOLLOW_CHANNEL, {
    client: getAuthApolloClient(),
  });

  const toggleFollow = useCallback(
    async (item: ChannelConnectionItem, shouldFollow: boolean) => {
      if (!userId) {
        navigate('/login');
        return;
      }
      if (!item.chId || useFollowChannelStore.getState().pendingByChannelId[item.chId]) {
        if (!item.chId) {
          setMenuItemId(null);
          setMenuCoords(null);
          showToast(t('channel.no_channel_to_follow', { defaultValue: 'This subscriber does not have a channel yet.' }), 'error');
        }
        return;
      }

      setMenuItemId(null);
      setMenuCoords(null);
      setFollowPending(item.chId, true);
      setFollowStatus(item.chId, shouldFollow);

      try {
        if (shouldFollow) {
          const response = await followChannel({ variables: { chId: item.chId } });
          if (response.error) {
            throw response.error;
          }
          const next = response.data?.followChannel?.isFollowing;
          setFollowStatus(item.chId, typeof next === 'boolean' ? next : true);
        } else {
          const response = await unfollowChannel({ variables: { chId: item.chId } });
          if (response.error) {
            throw response.error;
          }
          const next = response.data?.unfollowChannel?.isFollowing;
          setFollowStatus(item.chId, typeof next === 'boolean' ? next : false);
        }
        await refetch();
      } catch (err: unknown) {
        setFollowStatus(item.chId, !shouldFollow);
        showToast(
          getErrorMessage(err, t('common:something_went_wrong', { defaultValue: 'Something went wrong. Please try again.' })),
          'error',
        );
      } finally {
        setFollowPending(item.chId, false);
      }
    },
    [followChannel, navigate, refetch, setFollowPending, setFollowStatus, t, unfollowChannel, userId],
  );

  const shareItem = useCallback((item: ChannelConnectionItem) => {
    setMenuItemId(null);
    setMenuCoords(null);
    if (!item.chId) {
      return;
    }
    const shareUrl = `${window.location.origin}${channelURL(item.chId)}`;
    if (navigator.share) {
      void navigator.share({
        title: displayName(item),
        text: item.chDescription || displayName(item),
        url: shareUrl,
      });
      return;
    }
    void navigator.clipboard.writeText(shareUrl);
    showToast(t('channel.share_copied', { defaultValue: 'Channel link copied!' }), 'success');
  }, [t]);

  const followStatusById = useFollowChannelStore((state) => state.statusByChannelId);
  const openMenuItem = items.find((item) => (item.chId || item.userInfo?.uId || displayName(item)) === menuItemId) || null;

  const renderMenu = (item: ChannelConnectionItem) => {
    if (!hasOwnChannel(item)) {
      return null;
    }

    const itemKey = item.chId || item.userInfo?.uId || displayName(item);

    return (
      <div className={classNames(styles.menuWrap, { [styles.menuOpen]: menuItemId === itemKey })} ref={menuItemId === itemKey ? menuRef : undefined}>
        <button
          type="button"
          className={styles.moreButton}
          aria-label={t('channel.more_actions', { defaultValue: 'More actions' })}
          onClick={(event) => {
            event.stopPropagation();
            const button = event.currentTarget;
            if (menuItemId === itemKey) {
              setMenuItemId(null);
              setMenuCoords(null);
              return;
            }
            const rect = button.getBoundingClientRect();
            setMenuCoords({
              top: rect.bottom + 8,
              right: Math.max(8, window.innerWidth - rect.right),
            });
            setMenuItemId(itemKey);
          }}
        >
          <Icon icon={MoreIcon} />
        </button>
      </div>
    );
  };

  const renderMenuDropdown = () => {
    if (!openMenuItem || !menuCoords) {
      return null;
    }

    const item = openMenuItem;
    const isFollowed = followStatusById[item.chId] ?? !!item.isFollowedByMe;
    const isSelf = Boolean(userId && item.userInfo?.uId === userId) || Boolean(item.chId && item.chId === myChannelId);
    const canFollow = Boolean(item.chId) && !isSelf;
    const canShare = Boolean(item.chId);
    const canReport = canReportChannel && !item.isReported && !isSelf;
    const followLabel = isOwnChannel && connectionType === 'FOLLOWERS' && !isFollowed
      ? t('channel.follow_back_label')
      : isFollowed
        ? t('channel.unfollow_label')
        : t('channel.follow_label');
    const FollowActionIcon = isFollowed ? UnfollowIcon : FollowIcon;

    return createPortal(
      <div
        ref={dropdownRef}
        className={styles.dropdown}
        style={{ top: menuCoords.top, right: menuCoords.right }}
      >
        {canFollow && (
          <>
            <button
              type="button"
              className={styles.dropdownItem}
              onClick={(event) => {
                event.stopPropagation();
                void toggleFollow(item, !isFollowed);
              }}
            >
              <Icon icon={FollowActionIcon} className={styles.followMenuIcon} />
              {followLabel}
            </button>
            {(canShare || canReport) && <div className={styles.dropdownDivider} />}
          </>
        )}
        {canShare && (
          <>
            <button
              type="button"
              className={styles.dropdownItem}
              onClick={(event) => {
                event.stopPropagation();
                shareItem(item);
              }}
            >
              <Icon icon={ShareIcon} className={styles.shareMenuIcon} />
              {t('channel.share_label')}
            </button>
            {canReport && <div className={styles.dropdownDivider} />}
          </>
        )}
        {canReport && (
          <button
            type="button"
            className={styles.dropdownItem}
            onClick={(event) => {
              event.stopPropagation();
              setMenuItemId(null);
              setMenuCoords(null);
              setReportItem(item);
            }}
          >
            <Icon icon={FlagIcon} className={styles.reportMenuIcon} />
            {t('channel.report_button')}
          </button>
        )}
      </div>,
      document.body,
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <IconButton
            aria-label={t('channel.back_to_channel', { defaultValue: 'Back to channel' })}
            onClick={() => {
              const url = channelURL(chId);
              navigate(url || -1);
            }}
            className={styles.backButton}
          >
            <Icon icon={ArrowLeft} />
          </IconButton>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>{t('channel.all_subscribers_label')}</h1>
          <div className={styles.headerSearch}>
            <ChannelSearchBar
              searchActive={searchActive}
              onSearchActiveChange={setSearchActive}
              onQueryChange={handleSearchQueryChange}
              searchQuery={searchQuery}
              collapseOnClose
            />
          </div>
        </div>
        <div className={styles.tabs}>
          <button
            type="button"
            className={classNames(styles.tab, { [styles.activeTab]: connectionType === 'FOLLOWING' })}
            onClick={() => {
              setConnectionType('FOLLOWING');
              setMenuItemId(null);
            }}
          >
            {t('channel.following_tab', { count: followingCount, defaultValue: `Following (${followingCount})` })}
          </button>
          <button
            type="button"
            className={classNames(styles.tab, { [styles.activeTab]: connectionType === 'FOLLOWERS' })}
            onClick={() => {
              setConnectionType('FOLLOWERS');
              setMenuItemId(null);
            }}
          >
            {t('channel.followers_tab', { count: followersCount, defaultValue: `Followers (${followersCount})` })}
          </button>
        </div>

      {isInitialLoading ? (
        <div className={styles.loadingWrap}>
          <Spinner size="medium" />
        </div>
      ) : items.length === 0 ? (
        <div className={styles.emptyState}>
          <IllustrationIcon className={styles.emptyIcon} aria-hidden="true" />
          {debouncedSearch.trim() ? (
            <div className={styles.emptyTitle}>{t('channel.no_results')}</div>
          ) : (
            <>
              <h2 className={styles.emptyTitle}>
                {connectionType === 'FOLLOWING'
                  ? t('channel.following_empty_title')
                  : t('channel.followers_empty_title')}
              </h2>
              <p className={styles.emptyDescription}>
                {connectionType === 'FOLLOWING'
                  ? t('channel.following_empty_description')
                  : t('channel.followers_empty_description')}
              </p>
            </>
          )}
        </div>
      ) : (
        <ul className={styles.list}>
          {items.map((item) => {
            const itemKey = item.chId || item.userInfo?.uId || displayName(item);
            const handle = displayHandle(item);
            const name = displayName(item);
            const canOpenChannel = hasOwnChannel(item);
            return (
              <li key={itemKey} className={classNames(styles.row, { [styles.rowNoChannel]: !canOpenChannel })}>
                <button
                  type="button"
                  className={styles.rowMain}
                  onClick={() => {
                    if (canOpenChannel) {
                      navigate(channelURL(item.chId));
                    }
                  }}
                  disabled={!canOpenChannel}
                >
                  <img src={avatarSrc(item)} alt="" className={styles.avatar} />
                  <div className={styles.rowCopy}>
                    <div className={styles.name}>{name}</div>
                    {canOpenChannel ? (
                      <>
                        <div className={styles.meta}>
                          {[
                            handle,
                            t('channel.subscribers_followers', { count: item.followersCount ?? 0, defaultValue: `${item.followersCount ?? 0} Followers` }),
                            t('channel.subscribers_following', { count: item.followingCount ?? 0, defaultValue: `${item.followingCount ?? 0} Following` }),
                            t('channel.subscribers_videos', { count: item.videosCount ?? 0, defaultValue: `${item.videosCount ?? 0} Videos` }),
                          ].filter(Boolean).join(' | ')}
                        </div>
                        {item.chDescription ? <p className={styles.description}>{item.chDescription}</p> : null}
                      </>
                    ) : null}
                  </div>
                </button>
                {renderMenu(item)}
              </li>
            );
          })}
        </ul>
      )}

      {items.length < totalCount && (
        <div ref={loadMoreRef} className={styles.loadMore}>
          {(loadingMore || networkStatus === 3) && <Spinner size="small" />}
        </div>
      )}
      </div>

      {renderMenuDropdown()}

      {reportItem?.chId && (
        <ReportChannelModal
          channelId={reportItem.chId}
          onClose={() => setReportItem(null)}
          onSuccess={() => {
            setReportItem(null);
            void refetch();
          }}
        />
      )}
    </div>
  );
};

export default AllSubscribersPage;
